import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '@codedrill/shared';
import { useAuth } from '../../hooks/useAuth';
import UserAvatar from '../ui/UserAvatar';

interface ProfileDropdownProps {
  user: AuthUser;
  onClose: () => void;
}

export default function ProfileDropdown({ user, onClose }: ProfileDropdownProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function go(path: string) {
    navigate(path);
    onClose();
  }

  return (
    <div className="profile-dropdown" ref={ref} role="menu">
      <div className="profile-dropdown-header">
        <UserAvatar avatarUrl={user.avatarUrl} username={user.username} size="md" />
        <div className="profile-dropdown-info">
          <span className="profile-dropdown-name">{user.username}</span>
          <span className="profile-dropdown-email">{user.email}</span>
        </div>
      </div>

      <div className="profile-dropdown-divider" />

      <button className="profile-dropdown-item" role="menuitem" onClick={() => go('/profile')}>
        My Profile
      </button>
      <button className="profile-dropdown-item" role="menuitem" onClick={() => go('/settings')}>
        Settings
      </button>
      <button className="profile-dropdown-item" role="menuitem" onClick={() => go('/help')}>
        Help &amp; Support
      </button>

      <div className="profile-dropdown-divider" />

      <button
        className="profile-dropdown-item profile-dropdown-item--danger"
        role="menuitem"
        onClick={() => { void logout(); onClose(); }}
      >
        Logout
      </button>
    </div>
  );
}
